import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle2, 
  Linkedin, 
  Copy, 
  Share2, 
  ArrowRight,
  Sparkles,
  ExternalLink,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/App";

export default function ExpertOnboardingSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [expertData, setExpertData] = useState(null);

  // Get expert data from navigation state or localStorage
  useEffect(() => {
    const stateData = location.state?.expertData;
    if (stateData) {
      setExpertData(stateData);
      localStorage.setItem('newExpertData', JSON.stringify(stateData));
    } else {
      const stored = localStorage.getItem('newExpertData');
      if (stored) {
        setExpertData(JSON.parse(stored));
      }
    }
  }, [location]);

  const expertName = expertData?.name || user?.name || "Expert";
  const expertTitle = expertData?.title || "Industry Expert";
  const expertId = expertData?.id || "";
  const profileUrl = `https://expertbase.ai/experts/${expertId}`;

  // LinkedIn announcement template
  const linkedInTemplate = `🚀 Exciting News! I'm now available for expert consultations on ExpertBase!

As ${expertTitle}, I'm offering 1-on-1 sessions to help founders, entrepreneurs, and business leaders with:

✨ Strategic guidance and industry insights
✨ Problem-solving and decision-making support  
✨ Mentorship and career advice

Whether you're building a startup, scaling your business, or navigating career transitions - I'm here to help you succeed.

📅 Book a session with me: ${profileUrl}

#ExpertBase #Consulting #Mentorship #BusinessAdvice #Expertise #ProfessionalGrowth`;

  const [customTemplate, setCustomTemplate] = useState(linkedInTemplate);

  useEffect(() => {
    setCustomTemplate(linkedInTemplate);
  }, [expertData, user]);

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(customTemplate);
    setCopied(true);
    toast.success("Template copied to clipboard!");
    setTimeout(() => setCopied(false), 3000);
  };

  const handleShareLinkedIn = () => {
    // LinkedIn share URL with the profile link
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`;
    window.open(shareUrl, '_blank', 'width=600,height=600');
    toast.success("Opening LinkedIn - paste your announcement!");
  };

  const handleOpenLinkedInPost = () => {
    // Open LinkedIn's new post composer
    window.open('https://www.linkedin.com/feed/?shareActive=true', '_blank');
    toast.info("LinkedIn opened - paste your copied template!");
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Celebration Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold-500/20 via-transparent to-transparent" />
        <div className="absolute top-10 left-1/4 w-64 h-64 bg-gold-500/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-1/4 w-48 h-48 bg-gold-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="w-24 h-24 bg-gradient-to-br from-gold-500 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle2 className="w-12 h-12 text-black" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-display text-4xl md:text-5xl mb-4"
          >
            Welcome to <span className="text-gold-gradient">ExpertBase</span>! 🎉
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-slate-400 mb-8"
          >
            Your expert profile is live. Now let's spread the word!
          </motion.p>
        </div>
      </div>

      {/* LinkedIn Sharing Section */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-charcoal border border-slate-800 rounded-sm overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0077B5]/20 to-[#0077B5]/10 border-b border-slate-800 p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#0077B5] rounded-sm flex items-center justify-center">
                <Linkedin className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="font-display text-2xl mb-1">Announce on LinkedIn</h2>
                <p className="text-slate-400">Share your new expert profile with your network</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Template Preview */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-300">
                  <Sparkles className="w-4 h-4 inline mr-2 text-gold-500" />
                  Your Announcement Template
                </label>
                <span className="text-xs text-slate-500">Feel free to customize!</span>
              </div>
              <Textarea
                value={customTemplate}
                onChange={(e) => setCustomTemplate(e.target.value)}
                className="bg-obsidian border-slate-700 min-h-[280px] text-sm leading-relaxed"
                placeholder="Your LinkedIn announcement..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleCopyTemplate}
                variant="outline"
                className="flex-1 border-slate-700 hover:border-gold-500 py-6"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-2" />
                    Copy Template
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleOpenLinkedInPost}
                className="flex-1 bg-[#0077B5] hover:bg-[#006699] text-white py-6"
              >
                <Linkedin className="w-5 h-5 mr-2" />
                Open LinkedIn & Post
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Instructions */}
            <div className="bg-obsidian/50 border border-slate-800 rounded-sm p-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Share2 className="w-4 h-4 text-gold-500" />
                Quick Steps:
              </h4>
              <ol className="space-y-2 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-gold-500/20 text-gold-500 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                  Click "Copy Template" to copy your announcement
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-gold-500/20 text-gold-500 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                  Click "Open LinkedIn & Post" to go to LinkedIn
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-gold-500/20 text-gold-500 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                  Paste (Ctrl+V / Cmd+V) your template and post!
                </li>
              </ol>
            </div>

            {/* Profile Link */}
            <div className="flex items-center justify-between p-4 bg-gold-500/10 border border-gold-500/30 rounded-sm">
              <div>
                <p className="text-sm text-slate-400 mb-1">Your public profile:</p>
                <p className="text-gold-500 font-medium">{profileUrl}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(profileUrl);
                  toast.success("Profile link copied!");
                }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Skip / Continue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-4 mt-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate("/expert-dashboard")}
            className="text-slate-400 hover:text-white"
          >
            Skip for now
          </Button>
          <Button
            onClick={() => navigate("/expert-dashboard")}
            className="bg-gold-500 hover:bg-gold-400 text-black"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}
